import { FormEvent, ReactNode, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { Edit, Plus, Trash2 } from 'lucide-react';
import {
  useCreateChannelScrapingWorkerServer,
  useDeleteChannelScrapingWorkerServer,
  useUpdateChannelScrapingWorkerServer,
} from '@/hooks/useChannelScraping';
import { SCRAPING_SERVER_QUERY_PARAM, useSelectedScrapingServer } from '@/hooks/useSelectedScrapingServer';
import { ChannelScrapingWorkerServer } from '@/services/channelScrapingService';

interface TVChannelScrapingLayoutProps {
  children: ReactNode;
}

const navItems = [
  { to: '/content/tv-channels/scraping/tasks', label: 'Tareas', mobileLabel: 'Tareas' },
  { to: '/content/tv-channels/scraping/servers', label: 'Servidores Flussonic', mobileLabel: 'Flussonic' },
];

interface WorkerServerFormState {
  name: string;
  baseUrl: string;
  enabled: boolean;
  notes: string;
}

const emptyServerForm: WorkerServerFormState = {
  name: '',
  baseUrl: '',
  enabled: true,
  notes: '',
};

const TVChannelScrapingLayout = ({ children }: TVChannelScrapingLayoutProps) => {
  const {
    enabledScrapingServers,
    isScrapingServersLoading,
    selectedScrapingServer,
    selectedScrapingServerId,
    setSelectedScrapingServerId,
  } = useSelectedScrapingServer();
  const serverOptions = enabledScrapingServers.map((server) => ({ value: server.id, label: server.name }));
  const createServerMutation = useCreateChannelScrapingWorkerServer();
  const updateServerMutation = useUpdateChannelScrapingWorkerServer();
  const deleteServerMutation = useDeleteChannelScrapingWorkerServer();
  const [isServerModalOpen, setIsServerModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<ChannelScrapingWorkerServer | null>(null);
  const [serverForm, setServerForm] = useState<WorkerServerFormState>(emptyServerForm);
  const [serverSubmitError, setServerSubmitError] = useState<string | null>(null);
  const isServerSubmitting = createServerMutation.isPending || updateServerMutation.isPending;
  const canEditSelectedServer = Boolean(selectedScrapingServer) && selectedScrapingServer?.readOnly !== true;

  const fallbackServerId = useMemo(
    () => enabledScrapingServers.find((server) => server.id !== selectedScrapingServerId)?.id || '',
    [enabledScrapingServers, selectedScrapingServerId]
  );

  const openCreateServerModal = () => {
    setEditingServer(null);
    setServerForm(emptyServerForm);
    setServerSubmitError(null);
    setIsServerModalOpen(true);
  };

  const openEditServerModal = () => {
    if (!selectedScrapingServer || selectedScrapingServer.readOnly === true) {
      return;
    }
    setEditingServer(selectedScrapingServer);
    setServerForm({
      name: selectedScrapingServer.name,
      baseUrl: selectedScrapingServer.baseUrl,
      enabled: selectedScrapingServer.enabled !== false,
      notes: selectedScrapingServer.notes || '',
    });
    setServerSubmitError(null);
    setIsServerModalOpen(true);
  };

  const closeServerModal = () => {
    if (isServerSubmitting) return;
    setIsServerModalOpen(false);
    setEditingServer(null);
    setServerForm(emptyServerForm);
    setServerSubmitError(null);
  };

  const handleServerSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setServerSubmitError(null);
    try {
      if (editingServer) {
        await updateServerMutation.mutateAsync({ id: editingServer.id, data: serverForm });
        if (!serverForm.enabled) {
          setSelectedScrapingServerId(fallbackServerId);
        }
      } else {
        const response = await createServerMutation.mutateAsync(serverForm);
        setSelectedScrapingServerId(response.server.id);
      }
      closeServerModal();
    } catch (e) {
      setServerSubmitError(e instanceof Error ? e.message : 'No se pudo guardar el servidor de scraping');
    }
  };

  const handleDeleteSelectedServer = async () => {
    if (!selectedScrapingServer || selectedScrapingServer.readOnly === true) {
      return;
    }
    if (!window.confirm(`¿Eliminar servidor de scraping ${selectedScrapingServer.name}?`)) {
      return;
    }
    await deleteServerMutation.mutateAsync(selectedScrapingServer.id);
    setSelectedScrapingServerId(fallbackServerId);
  };

  return (
    <TVChannelsLayout>
      <div className="space-y-4">
        <div className="bg-white px-4 py-3 shadow">
          <div className="flex flex-col gap-2 md:flex-row md:items-end">
            <div className="w-full max-w-md">
              <Select
                label="Servidor de scraping"
                value={selectedScrapingServerId || ''}
                onChange={(event) => setSelectedScrapingServerId(event.target.value)}
                options={serverOptions}
                disabled={isScrapingServersLoading || !serverOptions.length}
                allowEmpty={!serverOptions.length}
                emptyOptionLabel={isScrapingServersLoading ? 'Cargando...' : 'No hay servidores activos'}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={openCreateServerModal}>
                <Plus className="h-4 w-4" />
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={openEditServerModal} disabled={!canEditSelectedServer}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="danger"
                onClick={() => void handleDeleteSelectedServer()}
                disabled={!canEditSelectedServer || deleteServerMutation.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="overflow-hidden bg-white shadow">
          <nav className="flex overflow-x-auto" aria-label="Channel scraping sections">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={{
                  pathname: item.to,
                  search: selectedScrapingServerId ? `${SCRAPING_SERVER_QUERY_PARAM}=${encodeURIComponent(selectedScrapingServerId)}` : '',
                }}
                className={({ isActive }) =>
                  `flex flex-1 items-center justify-center px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                <span className="hidden sm:inline">{item.label}</span>
                <span className="sm:hidden">{item.mobileLabel}</span>
              </NavLink>
            ))}
          </nav>
        </div>
        {children}
      </div>

      <Modal
        isOpen={isServerModalOpen}
        onClose={closeServerModal}
        title={editingServer ? 'Editar servidor de scraping' : 'Nuevo servidor de scraping'}
        size="lg"
        closeOnBackdrop={!isServerSubmitting}
        closeOnEsc={!isServerSubmitting}
      >
        <form onSubmit={handleServerSubmit} className="space-y-4 px-2 py-2">
          <Input label="Nombre" value={serverForm.name} onChange={(e) => setServerForm((prev) => ({ ...prev, name: e.target.value }))} required />
          <Input label="URL base" value={serverForm.baseUrl} onChange={(e) => setServerForm((prev) => ({ ...prev, baseUrl: e.target.value }))} required />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={serverForm.enabled}
              onChange={(e) => setServerForm((prev) => ({ ...prev, enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Activo
          </label>
          <Input label="Notas" value={serverForm.notes} onChange={(e) => setServerForm((prev) => ({ ...prev, notes: e.target.value }))} />
          {serverSubmitError && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{serverSubmitError}</div>}
          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <Button type="button" variant="outline" onClick={closeServerModal} disabled={isServerSubmitting}>Cancelar</Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={isServerSubmitting}>Guardar</Button>
          </div>
        </form>
      </Modal>
    </TVChannelsLayout>
  );
};

export default TVChannelScrapingLayout;
